import { PlatformFaqComponent } from './static-pages/platform-faq/platform-faq.component';
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { UserPermission } from './@core/enums/user-permission.enum';
import { ContactUsComponent } from './static-pages/contact-us/contact-us.component';
import { StaticPageComponent } from './static-pages/static-page/static-page.component';
import { NgxPermissionsGuard } from 'ngx-permissions';
import { ProceduresSettings } from './procedures/enums';
import { Settings } from './@core/enums/system-settings.enum';
import { AuthGuard } from './@core/auth/services';
import { UserProfileComponent } from './student/components/user-profile/user-profile.component';
import { NotFoundComponent } from './shared/components/not-found/not-found.component';
import { ErrorPageComponent } from './shared/components/error-page/error-page.component';
import { AboutUsComponent } from './static-pages/about-us/about-us.component';
import { SitemapComponent } from './static-pages/sitemap/sitemap.component';

const routes: Routes = [
  {
    path: '',
    canActivate: [NgxPermissionsGuard],
    data: {
      permissions: {
        only: [Settings.ProfileCompleted],
        redirectTo: '/myspace',
      },
    },
    children: [
      // `data.breadcrumb`: Unified Code breadcrumb steps after Home (see BreadcrumbService).
      { path: 'privacy', component: StaticPageComponent, data: { breadcrumb: [{ labelKey: 'unifiedShell.links.privacy' }] } },
      { path: 'terms', component: StaticPageComponent, data: { breadcrumb: [{ labelKey: 'unifiedShell.links.terms' }] } },
      { path: 'user-guide', component: StaticPageComponent, data: { breadcrumb: [{ labelKey: 'unifiedShell.links.userGuide' }] } },
      { path: 'trainee-guide', component: StaticPageComponent, data: { breadcrumb: [{ labelKey: 'unifiedShell.links.traineeGuide' }] } },
      { path: 'about', component: AboutUsComponent, data: { breadcrumb: [{ labelKey: 'unifiedShell.links.about' }] } },
      // The team page is rendered by the home module; StaticPageComponent has no page type for it
      // and used to request /settings/pages/type/undefined, which fails with a 500.
      { path: 'our-team', redirectTo: '/home/Our-Team', pathMatch: 'full' },
      // No static step: StaticPageComponent sets the CMS page title as the only step (Home > page title).
      { path: 'page/:id', component: StaticPageComponent, data: { breadcrumb: [] } },
      { path: 'contact', component: ContactUsComponent, data: { breadcrumb: [{ labelKey: 'unifiedShell.links.contact' }] } },
      { path: 'platform-faq', component: PlatformFaqComponent, data: { breadcrumb: [{ labelKey: 'unifiedShell.links.faq' }] } },
      { path: 'sitemap', component: SitemapComponent, data: { breadcrumb: [{ labelKey: 'unifiedShell.links.sitemap' }] } },
      {
        // Unified Code criterion 10: search results page (also reachable in Custom mode by URL).
        path: 'search',
        loadChildren: () =>
          import('./search/search.module').then((c) => c.SearchModule),
        data: { breadcrumb: [{ labelKey: 'unifiedFunctional.search.title' }] },
      },
      // The profile name is appended by UserProfileComponent through BreadcrumbService.set().
      { path: 'training-center/profile/:id', component: UserProfileComponent, data: { breadcrumb: [{ labelKey: 'unifiedShell.pages.trainingCenters' }] } },
      { path: 'instructor/profile/:id', component: UserProfileComponent, data: { breadcrumb: [{ labelKey: 'unifiedShell.pages.instructors' }] } },
      {
        path: 'join',
        loadChildren: () =>
          import('./auth/auth.module').then((c) => c.AuthModule),
      },
      {
        path: 'course',
        loadChildren: () =>
          import('./course/course.module').then((c) => c.CourseModule),
      },
      {
        path: 'learning-path',
        loadChildren: () =>
          import('./learning-path/learning-path.module').then(
            (c) => c.LearningPathModule
          ),
      },
      {
        path: 'notifications',
        loadChildren: () =>
          import('./notification/notification.module').then(
            (c) => c.NotificationModule
          ),
        data: { permissions: { only: [UserPermission.Student] } },
      },
      {
        path: 'check-out',
        loadChildren: () =>
          import('./payment/payment.module').then((c) => c.PaymentModule),
        data: { permissions: { only: [UserPermission.Student] } },
      },
      {
        path: 'onlineServices',
        canActivate: [NgxPermissionsGuard],
        loadChildren: () =>
          import('./procedures/procedures.module').then(
            (c) => c.ProceduresModule
          ),
        data: { permissions: { only: [ProceduresSettings.ShowProcedures] } },
      },
      {
        // A training plan and its statistics are the signed-in trainee's own data; without the guard
        // an anonymous visitor reached the screens and every request answered 401.
        path: 'training-plan',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./training-plan/training-plan.module').then(
            (c) => c.TrainingPlanModule
          ),
      },
      {
        path: 'home',
        loadChildren: () =>
          import('./home/modules/home.module').then(
            (c) => c.HomeModule
          ),
      },
      { path: 'digital-library', loadChildren: () => 
        import('./digital-library/digital-library.module').then(
          (c) => c.DigitalLibraryModule) 
      },
      { path: '', redirectTo: '/home', pathMatch: 'full' },
    ],
  },
  {
    path: 'myspace',
    // The personal area needs a session: without the guard an anonymous visitor reached the page,
    // the API answered 401 and the visitor ended up on the 404 page instead of the sign-in screen.
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./student/student.module').then((c) => c.StudentModule),
  },
  { path: '500', component: ErrorPageComponent },
  { path: '404', component: NotFoundComponent },
  { path: '**', redirectTo: '/404', pathMatch: 'full' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
